"use client"

import type React from "react"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { AlertCircle } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { EmailExistsPopup } from "./email-exists-popup"
import {EmailRegisteredPopup} from "@/components/email-registered-popup";
import {RegisterErrorPopup} from "@/components/register-error-popup";
import {loadRecaptcha} from "@/lib/recaptcha";

export default function SignupForm() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const [emailInput, setEmailInput] = useState("")
  const [isEmailExistsPopupOpen, setIsEmailExistsPopupOpen] = useState(false)
  const [isError, setIsError] = useState('')
  const [isEmailRegisteredPopupOpen, setIsEmailRegisteredPopupOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return re.test(String(email).toLowerCase())
  }

  const validatePassword = (password: string) => {
    return password.length >= 8
  }

  const validatePhoneNumber = (phone: string) => {
    return /^[89]\d{7}$/.test(phone)
  }

  const register = async (name: string, email: string, phone: string, password: string) => {
    setIsLoading(true);
    const recaptchaToken = await loadRecaptcha();
    const response = await fetch('/auth/api/register', {
      method: 'POST',
      body: JSON.stringify({name, email, phone, password, recaptchaToken}),
      headers: {
        "Content-Type": "application/json",
      }
    });

    setIsLoading(false);
    if (!response.ok) {
      const body = await response.json();
      if (body.emailExists) {
        setIsEmailExistsPopupOpen(true);
      } else {
        setIsError(body.error);
      }
    } else {
      setIsEmailRegisteredPopupOpen(true);
    }
  }

  useEffect(() => {
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address")
    } else {
      setEmailError("")
    }
  }, [email])

  useEffect(() => {
    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      setPhoneError("Please enter a valid Singapore phone number (8 digits, starting with 8 or 9)")
    } else {
      setPhoneError("")
    }
  }, [phoneNumber])

  useEffect(() => {
    if (password && !validatePassword(password)) {
      setPasswordError("Password must be at least 8 characters long")
    } else if (confirmPassword && password !== confirmPassword) {
      setPasswordError("Passwords do not match")
    } else {
      setPasswordError("")
    }
  }, [password, confirmPassword])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!name || !email || !phoneNumber || !password || !confirmPassword) {
      setError("Please fill in all fields.")
      return
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.")
      return
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError("Please enter a valid Singapore phone number.")
      return
    }

    if (!validatePassword(password)) {
      setError("Password must be at least 8 characters long.")
      return
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }

    // Simulate email exists check
    register(name, email, phoneNumber, password);
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-10 w-10 text-primary"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </div>
        <CardTitle className="text-2xl font-bold text-center">Create New Account</CardTitle>
        <CardDescription className="text-center">Enter your details to create a new account.</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your name"
              value={name}
              disabled={isLoading}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={emailInput}
              disabled={isLoading}
              onChange={(e) => {
                setEmailInput(e.target.value)
                setEmail(e.target.value)
              }}
              className={emailError ? "border-red-500" : ""}
            />
            {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter your Singapore phone number"
              value={phoneNumber}
              disabled={isLoading}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, "")
                setPhoneNumber(numericValue)
              }}
              className={phoneError ? "border-red-500" : ""}
              maxLength={8}
            />
            {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              disabled={isLoading}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={passwordError ? "border-red-500" : ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              disabled={isLoading}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={passwordError ? "border-red-500" : ""}
            />
            {passwordError && <p className="text-red-500 text-sm mt-1">{passwordError}</p>}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={!!emailError || !!passwordError || !!phoneError}>
            {isLoading ? "Creating Account..." : "Create Account"}
          </Button>
          <div className="text-sm text-center">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:underline">
              Log in
            </Link>
          </div>
        </CardFooter>
      </form>
      <EmailExistsPopup
        isOpen={isEmailExistsPopupOpen}
        onClose={() => setIsEmailExistsPopupOpen(false)}
        email={email}
      />
      <RegisterErrorPopup
        isOpen={isError != ''}
        onClose={() => setIsError('')}
        error={isError}
      />
      <EmailRegisteredPopup
        isOpen={isEmailRegisteredPopupOpen}
        onClose={() => setIsEmailExistsPopupOpen(false)}
      />
    </Card>
  )
}

